import {
  Card,
  FormControl,
  FormLabel,
  Box,
  Text,
  Button,
  HStack,
  Badge,
  Flex,
  Icon
} from '@chakra-ui/react';
import { FiStar, FiArrowRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { subscriptionAtom, subscriptionLoadingAtom } from '../../../atoms/v0/subscriptionAtoms';
import { useState, useEffect } from 'react';

export const SubscriptionCard = () => {
  const { t } = useTranslation(['v0/me', 'v0/subscription', 'v0/common']);
  const navigate = useNavigate();
  const [subscription] = useAtom(subscriptionAtom);
  const [isLoading] = useAtom(subscriptionLoadingAtom);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('free');
  const [expiryDate, setExpiryDate] = useState<string | null>(null);

  useEffect(() => {
    if (subscription) {
      setSubscriptionStatus(subscription.status);

      // 处理过期日期
      if (subscription.currentPeriodEnd) {
        // 转换为Date对象
        let date: Date;
        try {
          // 尝试将currentPeriodEnd作为毫秒时间戳处理
          date = new Date(subscription.currentPeriodEnd);
          // 检查日期是否有效
          if (isNaN(date.getTime())) {
            // 如果无效，尝试将其视为秒级时间戳
            date = new Date(Number(subscription.currentPeriodEnd) * 1000);
          }
          setExpiryDate(date.toLocaleDateString());
        } catch (error) {
          console.error('Error parsing date:', error);
        }
      }
    }
  }, [subscription]);

  // 获取状态显示
  const getStatusDisplay = () => {
    if (isLoading) return t('v0/common:loadingText');

    switch (subscriptionStatus) {
      case 'active':
        return t('v0/subscription:status.active');
      case 'trialing':
        return t('v0/subscription:status.trial');
      case 'canceled':
        return t('v0/subscription:status.canceled');
      case 'past_due':
        return t('v0/subscription:status.pastDue');
      default:
        return t('v0/subscription:status.free');
    }
  };

  // 获取状态颜色
  const getStatusColor = () => {
    switch (subscriptionStatus) {
      case 'active':
      case 'trialing':
        return 'green';
      case 'canceled':
      case 'past_due':
        return 'red';
      default:
        return 'gray';
    }
  };

  const isSubscribed = ['active', 'trialing'].includes(subscriptionStatus);

  return (
    <Card p={4} borderRadius="lg" boxShadow="sm">
      <FormControl display="flex" flexDirection="column">
        <Flex alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <FormLabel htmlFor="subscription" mb={0} fontWeight="medium">
              {t('v0/me:subscription.title')}
            </FormLabel>
            <Text fontSize="sm" color="gray.500">
              {t('v0/me:subscription.description')}
            </Text>
          </Box>

          <HStack>
            <Badge colorScheme={getStatusColor()}>
              {getStatusDisplay()}
            </Badge>
          </HStack>
        </Flex>

        {isSubscribed && expiryDate && (
          <Text fontSize="sm" color="gray.500" mb={2}>
            {t('v0/subscription:management.expiresOn', { date: expiryDate })}
          </Text>
        )}

        <Button
          onClick={() => navigate('/subscription')}
          colorScheme={isSubscribed ? 'gray' : 'teal'}
          variant={isSubscribed ? 'outline' : 'solid'}
          size="sm"
          rightIcon={<Icon as={FiArrowRight} />}
          leftIcon={<Icon as={FiStar} />}
          alignSelf="flex-end"
        >
          {isSubscribed
            ? t('v0/me:subscription.manage')
            : t('v0/me:subscription.upgrade')}
        </Button>
      </FormControl>
    </Card>
  );
};